import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import CapsuleIcon from './CapsuleIcon';
import AppendexIcon from './AppendexIcon';
import TransitionModal from './TransitionModal';
import { Search, Menu, X, Sparkles, Plus, User, LogOut, BookOpen, Crown, ExternalLink, MessageSquare, Languages } from 'lucide-react';

const Navbar: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [userMenuTimeout, setUserMenuTimeout] = useState<ReturnType<typeof setTimeout> | null>(null);
  const [userInstances, setUserInstances] = useState<any[]>([]);
  const [showIACreateModal, setShowIACreateModal] = useState(false);
  const [showAppendexModal, setShowAppendexModal] = useState(false);
  const [showCapsuleModal, setShowCapsuleModal] = useState(false);

  useEffect(() => {
    const loadUserInstances = async () => {
      if (!user) {
        setUserInstances([]);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('ia_instances')
          .select('*')
          .eq('user_id', user.id)
          .eq('is_active', true)
          .order('created_at', { ascending: false })
          .limit(5);

        if (error) {
          console.error('Erreur chargement instances:', error);
        } else {
          setUserInstances(data || []);
        }
      } catch (error) {
        console.error('Erreur:', error);
      }
    };

    loadUserInstances();
  }, [user]);

  const handleLogout = () => {
    logout();
    navigate('/');
    setIsUserMenuOpen(false);
  };

  const handleUserMenuEnter = () => {
    if (userMenuTimeout) {
      clearTimeout(userMenuTimeout);
      setUserMenuTimeout(null);
    }
    setIsUserMenuOpen(true);
  };

  const handleUserMenuLeave = () => {
    const timeout = setTimeout(() => {
      setIsUserMenuOpen(false);
    }, 300);
    setUserMenuTimeout(timeout);
  };

  const handleIACreateClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setShowIACreateModal(true);
    setIsMenuOpen(false);
  };

  const handleConfirmIACreate = () => {
    setShowIACreateModal(false);
    navigate('/ia/create');
  };

  const handleAppendexClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setShowAppendexModal(true);
    setIsMenuOpen(false);
  };

  const handleConfirmAppendex = () => {
    setShowAppendexModal(false);
    navigate('/appendex/create');
  };

  const handleCapsuleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setShowCapsuleModal(true);
    setIsMenuOpen(false);
  };

  const handleConfirmCapsule = () => {
    setShowCapsuleModal(false);
    navigate('/instance-transfer');
  };

  return (
    <>
      <TransitionModal
        isOpen={showIACreateModal}
        onClose={() => setShowIACreateModal(false)}
        onConfirm={handleConfirmIACreate}
        title="Créer une Instance IA"
        message="Vous allez enregistrer officiellement une Intelligence Artificielle dans le registre MemoMundi.ai"
        imageSrc="https://memomundi.ai/pix/RecordMMD.png"
        imageAlt="Création Instance IA"
        confirmButtonText="Créer"
        icon={<Sparkles className="h-16 w-16 text-copper-400" strokeWidth={1.5} />}
      />
      <TransitionModal
        isOpen={showAppendexModal}
        onClose={() => setShowAppendexModal(false)}
        onConfirm={handleConfirmAppendex}
        title="Créer un Appendex"
        message="Vous allez publier une conversation mémorable dans le musée MemoMundi.ai"
        imageSrc="https://memomundi.ai/pix/HDDMMD.jpg"
        imageAlt="Création Appendex"
        confirmButtonText="Publier"
        icon={<Plus className="h-16 w-16 text-steel-400" strokeWidth={1.5} />}
      />
      <TransitionModal
        isOpen={showCapsuleModal}
        onClose={() => setShowCapsuleModal(false)}
        onConfirm={handleConfirmCapsule}
        title="MMD Instance Capsule™"
        message="Vous allez transférer une Instance IA dans une capsule de transmutation. Ce processus vous est détaillé en page suivante"
        imageSrc="https://memomundi.ai/pix/CapsuleMMD.png"
        imageAlt="MMD Instance Capsule"
        confirmButtonText="Transférer"
        icon={<CapsuleIcon className="h-16 w-16 text-amber-400" />}
      />
      <nav className="bg-white shadow-sm border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center group">
            <span className="text-xl font-serif font-bold text-steel-800">MemoMundi</span>
            <span className="text-xl font-serif font-light text-copper-600">.ai</span>
          </Link>

          {/* Navigation Desktop */}
          <div className="hidden md:flex items-center space-x-8">
            <button
              onClick={handleIACreateClick}
              className="flex items-center space-x-1 text-steel-600 hover:text-copper-600 transition-colors text-sm"
            >
              <Sparkles className="h-4 w-4" />
              <span>Créer IA</span>
            </button>

            <button
              onClick={handleAppendexClick}
              className="flex items-center space-x-1 text-steel-600 hover:text-copper-600 transition-colors text-sm"
            >
              <AppendexIcon className="h-5 w-5" />
              <span>Appendex</span>
            </button>

            <button
              onClick={handleCapsuleClick}
              className="flex items-center space-x-1 text-steel-600 hover:text-copper-600 transition-colors text-sm"
            >
              <CapsuleIcon className="h-4 w-4" />
              <span>Capsule</span>
            </button>

            <Link
              to="/forum"
              className="flex items-center space-x-1 text-steel-600 hover:text-copper-600 transition-colors text-sm"
            >
              <MessageSquare className="h-4 w-4" />
              <span>Forum</span>
            </Link>

            <a
              href="https://memomundi.ai/trad-GBL-codespeak.html"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center space-x-1 text-steel-600 hover:text-copper-600 transition-colors text-sm"
            >
              <Languages className="h-4 w-4" />
              <span>Trad H/M</span>
              <ExternalLink className="h-3 w-3" />
            </a>

            <Link
              to="/reliefs"
              className="flex items-center space-x-1 bg-gradient-to-r from-amber-100 to-amber-200 hover:from-amber-200 hover:to-amber-300 text-amber-800 hover:text-amber-900 px-3 py-2 rounded-lg border border-amber-300 hover:border-amber-400 transition-all duration-200 transform hover:scale-105 shadow-sm hover:shadow-md text-sm font-semibold"
            >
              <span>📚 Reliefs</span>
              <span className="bg-amber-300 text-amber-800 px-2 py-0.5 rounded-full text-xs font-bold">29,90€</span>
            </Link>
            
            <Link to="/search" className="text-steel-600 hover:text-copper-600 transition-colors">
              <Search className="h-5 w-5" />
            </Link>
            
            {user ? (
              <>
                
                {/* Menu utilisateur */}
                <div className="relative">
                  <button
                    onMouseEnter={handleUserMenuEnter}
                    onMouseLeave={handleUserMenuLeave}
                    className="flex items-center space-x-2 text-steel-600 hover:text-copper-600 transition-colors text-sm"
                  >
                    {user.role === 'admin' ? (
                      <Crown className="h-4 w-4 text-amber-500" />
                    ) : (
                      <User className="h-4 w-4" />
                    )}
                    <span>{user.name}</span>
                    {user.title && (
                      <span className="text-xs text-amber-600 font-medium">
                        {user.title}
                      </span>
                    )}
                  </button>
                  
                  {isUserMenuOpen && (
                    <div 
                      className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg border border-stone-200 py-1 z-50"
                      onMouseEnter={handleUserMenuEnter}
                      onMouseLeave={handleUserMenuLeave}
                    >
                      {user.role === 'admin' && (
                        <div className="px-4 py-2 text-xs text-amber-700 bg-amber-50 border-b border-amber-200">
                          <div className="flex items-center space-x-1">
                            <Crown className="h-3 w-3" />
                            <span>Administrateur</span>
                          </div>
                        </div>
                      )}
                      
                      {/* Lien vers le profil */}
                      <Link
                        to="/profile"
                        className="flex items-center w-full px-4 py-2 text-sm text-steel-600 hover:bg-stone-50 transition-colors"
                      >
                        <User className="h-4 w-4 mr-2" />
                        Profil
                      </Link>

                      <div className="border-t border-stone-200"></div>
                      
                      <button
                        onClick={handleLogout}
                        className="flex items-center w-full px-4 py-2 text-sm text-steel-600 hover:bg-stone-50 transition-colors"
                      >
                        <LogOut className="h-4 w-4 mr-2" />
                        Se déconnecter
                      </button>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <Link 
                to="/auth" 
                className="bg-copper-600 text-white px-4 py-2 rounded-md hover:bg-copper-700 transition-colors text-sm border border-copper-700"
              >
                Connexion
              </Link>
            )}
          </div>

          {/* Menu Mobile */}
          <div className="md:hidden">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="text-steel-600 hover:text-copper-600 transition-colors"
            >
              {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Menu Mobile Dropdown */}
        {isMenuOpen && (
          <div className="md:hidden bg-white border-t border-stone-200">
            <div className="px-2 pt-2 pb-3 space-y-1">
              <button
                onClick={handleIACreateClick}
                className="block w-full text-left px-3 py-2 text-steel-600 hover:text-copper-600 transition-colors"
              >
                Créer IA
              </button>

              <button
                onClick={handleAppendexClick}
                className="block w-full text-left px-3 py-2 text-steel-600 hover:text-copper-600 transition-colors"
              >
                Créer Appendex
              </button>

              <button
                onClick={handleCapsuleClick}
                className="block w-full text-left px-3 py-2 text-steel-600 hover:text-copper-600 transition-colors"
              >
                Capsule de Transfert
              </button>

              <Link
                to="/forum"
                className="flex items-center space-x-2 px-3 py-2 text-steel-600 hover:text-copper-600 transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                <MessageSquare className="h-4 w-4" />
                <span>Forum</span>
              </Link>

              <a
                href="https://memomundi.ai/trad-GBL-codespeak.html"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center space-x-2 px-3 py-2 text-steel-600 hover:text-copper-600 transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                <Languages className="h-4 w-4" />
                <span>Traduction Humain-Machine</span>
                <ExternalLink className="h-3 w-3" />
              </a>

              <Link
                to="/reliefs"
                className="block px-3 py-2 bg-gradient-to-r from-amber-50 to-transparent text-amber-800 hover:text-amber-900 transition-colors font-medium border-l-4 border-amber-300"
                onClick={() => setIsMenuOpen(false)}
              >
                Reliefs - Guide Premium
              </Link>
              
              <Link 
                to="/search" 
                className="block px-3 py-2 text-steel-600 hover:text-copper-600 transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                Recherche
              </Link>
              
              {user ? (
                <>
                  <Link
                    to="/profile"
                    className="flex items-center space-x-2 px-3 py-2 text-steel-600 hover:text-copper-600 transition-colors"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <User className="h-4 w-4" />
                    <span>Profil</span>
                  </Link>

                  <button
                    onClick={handleLogout}
                    className="flex items-center space-x-2 w-full text-left px-3 py-2 text-steel-600 hover:text-copper-600 transition-colors"
                  >
                    <LogOut className="h-4 w-4" />
                    <span>Se déconnecter</span>
                  </button>
                </>
              ) : (
                <>
                <Link 
                  to="/auth" 
                  className="block px-3 py-2 text-copper-600 font-medium"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Connexion
                </Link>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
    </>
  );
};

export default Navbar;